import { captureException } from '@sentry/react';
import { HTTPError, TimeoutError } from 'ky';
import { overlay } from 'overlay-kit';

import { ErrorDialog } from '@/shared/components/error-dialog';

const ERROR_TITLE = {
  NETWORK_ERROR: 'Network Error',
  UNKNOWN_ERROR: 'Unknown Error',
};

const ERROR_MESSAGES = {
  CLIENT_ERROR: 'Request failed. Please check your input and try again.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  TIMEOUT_ERROR: 'Request timed out. Please check your connection and try again.',
  GENERIC_ERROR: 'An unexpected error occurred. Please try again.',
  CONFLICT_ERROR: 'A conflicting operation is in progress. Please try again later.',
};

async function parseError(
  error: unknown,
): Promise<{ title: string; message: string; isRetryable: boolean }> {
  try {
    // Check if it's a ky HTTPError
    if (error instanceof HTTPError) {
      const status = error.response.status;

      // HTTP 409 - Conflict
      if (status === 409) {
        return {
          title: ERROR_TITLE.NETWORK_ERROR,
          message: ERROR_MESSAGES.CONFLICT_ERROR,
          isRetryable: true,
        };
      }

      // HTTP 5xx - Server errors
      if (status >= 500) {
        captureException(error);
        return {
          title: ERROR_TITLE.NETWORK_ERROR,
          message: ERROR_MESSAGES.SERVER_ERROR,
          isRetryable: true,
        };
      }

      // HTTP 4xx - Client errors
      if (status >= 400 && status < 500) {
        captureException(error);
        try {
          const data = await error.response.json();
          return {
            title: ERROR_TITLE.NETWORK_ERROR,
            message: data.message ?? ERROR_MESSAGES.CLIENT_ERROR,
            isRetryable: false,
          };
        } catch {
          return {
            title: ERROR_TITLE.NETWORK_ERROR,
            message: ERROR_MESSAGES.CLIENT_ERROR,
            isRetryable: false,
          };
        }
      }
    }

    // Check if it's a ky TimeoutError
    if (error instanceof TimeoutError) {
      return {
        title: ERROR_TITLE.NETWORK_ERROR,
        message: ERROR_MESSAGES.TIMEOUT_ERROR,
        isRetryable: true,
      };
    }

    captureException(error);

    return {
      title: ERROR_TITLE.UNKNOWN_ERROR,
      message: ERROR_MESSAGES.GENERIC_ERROR,
      isRetryable: true,
    };
  } catch (nestedError) {
    captureException(nestedError);

    return {
      title: ERROR_TITLE.UNKNOWN_ERROR,
      message: ERROR_MESSAGES.GENERIC_ERROR,
      isRetryable: true,
    };
  }
}

export async function showErrorDialog({
  error,
  title,
  message,
}: {
  error: unknown;
  title?: string;
  message?: string;
}): Promise<void> {
  const parsedError = await parseError(error);

  return overlay.openAsync(({ isOpen, close, unmount }) => (
    <ErrorDialog
      isOpen={isOpen}
      onClose={close}
      onUnmount={unmount}
      title={title}
      message={message ?? parsedError.message}
    />
  ));
}
